import { DateTypeFilter, getLocalTime } from '@gripp/shared-logic';
import { useRef } from 'react';
import { Controller } from 'react-hook-form';
import {
  KeyboardTypeOptions,
  Platform,
  Pressable,
  StyleProp,
  Text,
  TextProps,
  View,
  ViewStyle,
} from 'react-native';
import { IconButton } from 'react-native-paper';
import { useOpenedState } from '../../../hooks';
import { Colors } from '../../../themes';
import { StyledTextInput } from '../controlledTextInput';
import { datePickerStyle as styles } from './datePickerStyle';
import { DatePickerWheel } from './datePickerWheel';
import { DateRangePickerSelector } from './dateRangePickerSelector';
import { DateRangeSelectorProps } from './dateRangeSelector';
import { WebDateTimePicker } from './webDateTimePicker';

type DatePickerSelectorProps = DateRangeSelectorProps & {
  name: string;
  placeholder?: string;
  textArea?: boolean;
  innerRef?: any;
  keyboardType?: KeyboardTypeOptions;
  height?: number;
  label?: string;
  isStartDate?: boolean;
  containerStyle?: StyleProp<ViewStyle>;
  mode?: 'date' | 'time' | 'datetime';
  maxYears?: number;
  calendarIconStyle?: StyleProp<ViewStyle>;
  /** Optional style for the native date picker modal container (e.g. background). Applied on iOS. */
  pickerContainerStyleIOS?: StyleProp<ViewStyle>;
  /** Optional style for the native date picker modal content. Applied on iOS. */
  modalStyleIOS?: StyleProp<ViewStyle>;
  /** Optional style for the label above the date field. */
  labelStyle?: TextProps['style'];
  /** Optional color for the calendar/clock icon. */
  calendarIconColor?: string;
  /** Optional style for the iOS modal confirm button container. */
  confirmButtonStyleIOS?: StyleProp<ViewStyle>;
};

export const DatePickerSelectorOld = (props: DatePickerSelectorProps) => {
  const isWeb = Platform.OS === 'web';
  const datePickerStartDateRef = useRef<View>(null!);
  const {
    onOpen: onDateRangePickerSelectorOpen,
    onClose: onDateRangePickerSelectorClose,
    isOpened: isDateRangePickerSelectorOpened,
  } = useOpenedState();

  const handleDateRangeSelector = () => {
    props.setValue('dateType', DateTypeFilter.CUSTOMDATE);
    onDateRangePickerSelectorOpen();
  };

  if (props.mode === 'time' && isWeb) {
    return (
      <WebDateTimePicker
        control={props.control}
        setValue={props.setValue}
        isStartDate={props.isStartDate}
        name={props.name}
        label={props.label}
      />
    );
  }

  return (
    <Pressable
      style={[
        styles.dateRangeFieldContainer,
        isDateRangePickerSelectorOpened && styles.dateRangeFieldContainerActive,
        props.containerStyle,
      ]}
      onPress={handleDateRangeSelector}
    >
      <Controller
        control={props.control}
        name={props.name}
        render={({ field: { onChange, onBlur, value } }) => {
          const valueFormat =
            props.mode === 'time' && value ? getLocalTime(value) : value;
          return (
            <>
              {props.label && (
                <View style={[styles.labelContainer]}>
                  <Text
                    style={[styles.label, props.labelStyle]}
                    numberOfLines={1}
                  >
                    {props.label}
                  </Text>
                </View>
              )}
              <View style={styles.inputContainer}>
                <StyledTextInput
                  onChangeText={onChange}
                  onBlur={onBlur}
                  value={valueFormat ?? ''}
                  placeholder={props.placeholder}
                  editable={false}
                  textArea={props.textArea}
                  innerRef={props.innerRef}
                  keyboardType={props.keyboardType}
                  height={props.height}
                  backgroundColor={'transparent'}
                  inputInnerStyle={styles.inputInnerStyle}
                  pointerEvents="none"
                  disableInnerStyle
                />
                <IconButton
                  icon={props.mode === 'time' ? 'clock' : 'calendar-month'}
                  size={20}
                  iconColor={
                    props.calendarIconColor ?? Colors.grayText
                  }
                  style={[styles.calendarIcon, props.calendarIconStyle]}
                />
              </View>
              <View ref={datePickerStartDateRef}>
                {isWeb ? (
                  <DateRangePickerSelector
                    control={props.control}
                    setValue={props.setValue}
                    onDateSelected={onChange}
                    anchorRef={datePickerStartDateRef}
                    isVisible={isDateRangePickerSelectorOpened}
                    onClose={onDateRangePickerSelectorClose}
                    value={value}
                    isStartDate={props.isStartDate}
                    mode={props.mode}
                    maxYears={props.maxYears}
                    minDate={props.minDate}
                  />
                ) : (
                  <DatePickerWheel
                    control={props.control}
                    setValue={props.setValue}
                    onDateSelected={onChange}
                    isVisible={isDateRangePickerSelectorOpened}
                    onClose={onDateRangePickerSelectorClose}
                    isStartDate={props.isStartDate}
                    mode={props.mode}
                    name={props.name}
                    minDate={props.minDate}
                    pickerContainerStyleIOS={props.pickerContainerStyleIOS}
                    modalStyleIOS={props.modalStyleIOS}
                    confirmButtonStyleIOS={props.confirmButtonStyleIOS}
                  />
                )}
              </View>
            </>
          );
        }}
      />
    </Pressable>
  );
};
